import { useEffect, useRef, useState } from "react";
import { answerDemoToolCall } from "../fixtures/widgets";

/**
 * DemoWidgetFrame — the widget gallery's fixture host
 * (docs/widget-gallery-page.md §1/§2).
 *
 * Widget HTML source: the DEPLOYED-FETCH option. Each widget document is
 * fetched at view time from GET /mcp/ui/<name> — the same public,
 * no-auth endpoint Claude iframes (mcp/server.ts serveWidgetHtml), and
 * same-origin here because CloudFront proxies /mcp* onto the app domain
 * — then rendered via a sandboxed <iframe srcdoc>. Zero build plumbing,
 * always the shipped widget bytes.
 *
 * Mock host: this component plays the HOST side of the bootstrap's
 * postMessage handshake — the same dance the dashboard's WidgetFrame
 * speaks, because WIDGET_BOOTSTRAP_JS talks to window.parent (an
 * in-iframe script can't receive those messages; the "shim injected into
 * the srcdoc" is just the CSP guard below):
 *
 *   1) widget -> host:  ui/initialize request → answered locally
 *   2) widget -> host:  ui/notifications/initialized → fixture delivered
 *      as ui/notifications/tool-result { structuredContent }
 *   3) widget -> host:  ui/notifications/size-changed → frame resizes
 *   4) widget -> host:  tools/call → answered by the demo bridge
 *      (fixtures/widgets/demoBridge.ts): the paired data tool re-delivers
 *      the fixture, mutating tools resolve to visible demo-mode
 *      responses. NO network call ever leaves this page for a tenant
 *      endpoint — and the CSP <meta> injected into the srcdoc
 *      (default-src 'none') makes that a hard guarantee inside the
 *      iframe, not a convention.
 *
 * Frames lazy-load: fetch + mount happen when the section nears the
 * viewport (IntersectionObserver), so below-the-fold widgets cost
 * nothing on first paint.
 */

const PROTOCOL_VERSION = "2025-11-25"; // mirrors infra/lib/lambdas/mcp/server.ts
const DEFAULT_HEIGHT = 220;

// Belt-and-braces network guard injected into each srcdoc: the widget
// documents are fully self-contained (inline CSS/JS, no fonts, no
// fetches), so nothing legitimate is blocked — but if a future widget
// ever grew a network call, the gallery copy would fail closed.
const DEMO_CSP =
  `<meta http-equiv="Content-Security-Policy" content="` +
  `default-src 'none'; script-src 'unsafe-inline'; ` +
  `style-src 'unsafe-inline'; img-src data: blob:;">`;

type JsonRpcMessage = {
  jsonrpc?: string;
  id?: string | number | null;
  method?: string;
  params?: Record<string, unknown>;
};

// One fetch per widget name per page load, shared across re-mounts.
const htmlCache = new Map<string, Promise<string | null>>();
function fetchWidgetHtml(name: string): Promise<string | null> {
  let cached = htmlCache.get(name);
  if (!cached) {
    cached = fetch(`/mcp/ui/${encodeURIComponent(name)}`)
      .then((res) => {
        if (!res.ok) return null;
        const type = res.headers.get("content-type") || "";
        // A SPA-fallback index.html (vite preview without the backend)
        // is not a widget document — treat it as unavailable.
        return type.includes("text/html") ? res.text() : null;
      })
      .then((html) =>
        html && html.includes("ui/initialize")
          ? html.replace(/<head>/i, `<head>${DEMO_CSP}`)
          : null
      )
      .catch(() => null);
    htmlCache.set(name, cached);
  }
  return cached;
}

type Phase = "waiting" | "loading" | "ready" | "unavailable";

export function DemoWidgetFrame({
  widget,
  pairedTool,
  makeFixture,
  eager = false,
}: {
  /** Widget name as served at /mcp/ui/<name>, e.g. "session-status". */
  widget: string;
  /** The paired data tool — bridge calls to it re-deliver the fixture. */
  pairedTool: string;
  /** Fixture factory — fresh copy per mount (demo calls mutate it). */
  makeFixture: () => Record<string, unknown>;
  /** Skip lazy-loading (the above-the-fold first section). */
  eager?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [phase, setPhase] = useState<Phase>(eager ? "loading" : "waiting");
  const [html, setHtml] = useState<string | null>(null);
  const [height, setHeight] = useState(DEFAULT_HEIGHT);

  // The frame's mutable demo state — created once per mount so a Stop or
  // prompt response sticks across that frame's refreshes.
  const fixtureRef = useRef<Record<string, unknown> | null>(null);
  if (fixtureRef.current === null) fixtureRef.current = makeFixture();
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Lazy-load: flip waiting → loading when the section nears the viewport.
  useEffect(() => {
    if (phase !== "waiting") return;
    const el = hostRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setPhase("loading");
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setPhase("loading");
      },
      { rootMargin: "480px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  // Fetch the deployed widget document once visible.
  useEffect(() => {
    if (phase !== "loading") return;
    let disposed = false;
    fetchWidgetHtml(widget).then((doc) => {
      if (disposed) return;
      if (doc) {
        setHtml(doc);
        setPhase("ready");
      } else {
        setPhase("unavailable");
      }
    });
    return () => {
      disposed = true;
    };
  }, [phase, widget]);

  // The mock host: answer the handshake + bridge calls for OUR iframe only.
  useEffect(() => {
    if (phase !== "ready") return;
    const timers = timersRef.current;

    const post = (msg: Record<string, unknown>) => {
      try {
        // Sandboxed srcdoc iframes are opaque-origin — target must be "*".
        iframeRef.current?.contentWindow?.postMessage(msg, "*");
      } catch {
        /* iframe gone */
      }
    };

    const later = (ms: number, fn: () => void) => {
      const t = setTimeout(() => {
        timers.delete(t);
        fn();
      }, ms);
      timers.add(t);
    };

    const sendFixture = () =>
      post({
        jsonrpc: "2.0",
        method: "ui/notifications/tool-result",
        params: { structuredContent: fixtureRef.current },
      });

    const onMessage = (ev: MessageEvent) => {
      const iframe = iframeRef.current;
      if (!iframe || ev.source !== iframe.contentWindow) return;
      const msg = ev.data as JsonRpcMessage;
      if (!msg || msg.jsonrpc !== "2.0" || typeof msg.method !== "string") {
        return;
      }
      const params: Record<string, unknown> =
        msg.params && typeof msg.params === "object" ? msg.params : {};

      if (msg.method === "ui/initialize" && msg.id != null) {
        post({
          jsonrpc: "2.0",
          id: msg.id,
          result: {
            protocolVersion: PROTOCOL_VERSION,
            hostInfo: { name: "nightswarm-widget-gallery", version: "1.0.0" },
            hostContext: { theme: "dark" },
          },
        });
        sendFixture();
        return;
      }
      if (msg.method === "ui/notifications/initialized") {
        // Re-deliver so a widget that wired render() after its initialize
        // round-trip still gets the data (same as the dashboard host).
        sendFixture();
        return;
      }
      if (msg.method === "ui/notifications/size-changed") {
        const h = Number(params.height);
        if (Number.isFinite(h) && h > 0) setHeight(Math.ceil(h));
        return;
      }
      if (msg.method === "tools/call" && msg.id != null) {
        const id = msg.id;
        const name = typeof params.name === "string" ? params.name : "";
        const args =
          params.arguments && typeof params.arguments === "object"
            ? (params.arguments as Record<string, unknown>)
            : {};
        const answer = answerDemoToolCall(
          pairedTool,
          fixtureRef.current!,
          name,
          args
        );
        later(answer.delayMs, () => {
          if ("result" in answer) {
            post({ jsonrpc: "2.0", id, result: answer.result });
          } else {
            post({
              jsonrpc: "2.0",
              id,
              error: { code: -32000, message: answer.errorMessage },
            });
          }
        });
        return;
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      for (const t of timers) clearTimeout(t);
      timers.clear();
    };
  }, [phase, pairedTool]);

  return (
    <div ref={hostRef} className="wdg-frame" data-widget={widget}>
      {phase === "ready" && html ? (
        <iframe
          ref={iframeRef}
          title={`${widget} widget — live demo`}
          srcDoc={html}
          sandbox="allow-scripts"
          style={{ height }}
          loading="lazy"
        />
      ) : phase === "unavailable" ? (
        <div className="wdg-frame-fallback" role="note">
          <span className="mono">/mcp/ui/{widget}</span> didn't load — this
          gallery embeds the real deployed widget documents, so previews
          need the deployed backend (they're live on nightswarm.app).
        </div>
      ) : (
        <div className="wdg-frame-loading" style={{ height: DEFAULT_HEIGHT }}>
          <span className="mono">loading {widget}…</span>
        </div>
      )}
    </div>
  );
}
